import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import backIcon from "@/assets/paytm/back.png"
import walletIcon from "@/assets/paytm/wallet.png"
import info from "@/assets/paytm/info.png"
import Select from "@/components/features/paytm/select"
import DeliveryOption from "@/components/features/paytm/delivery-option"
import Input from "@/components/features/paytm/input"
import { Address, LocationAddress, Quote } from '@/lib/interfaces'
import Button from '@/components/features/paytm/button'
import { useMutation } from '@tanstack/react-query'
import { Api } from '@/lib/utils'
import { toast } from 'sonner'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Info } from 'lucide-react'

export const Route = createFileRoute('/shikhar/neworder/confirm')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): {
    dropOtp: string,
    rtoOtp: string,
    quotes: Quote[]
    quoteId: string
    walletBalance: number,
    storeId: string,
    customerLocation: LocationAddress
  } => {
    return {
      dropOtp: search.dropOtp as string,
      rtoOtp: search.rtoOtp as string,
      quotes: search.quotes as Quote[] || [],
      quoteId: search.quoteId as string || '',
      walletBalance: search.walletBalance as number,
      storeId: search.storeId as string,
      customerLocation: search.customerLocation as LocationAddress
    }
  }
})

function RouteComponent() {
  const [packageType, setPackageType] = useState('')
  const [billNumber, setBillNumber] = useState('')
  const [orderAmount, setOrderAmount] = useState('')

  const { dropOtp, rtoOtp, quotes, quoteId, walletBalance, storeId, customerLocation } = Route.useSearch()
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null)

  const router = useRouter()
  const navigate = useNavigate()
  const {token} = useStore(useShallow(state => ({token: state.token})))

  const createOrder = useMutation({
    mutationFn: () => {
      let payload: { [k: string]: string | number | string[] | { [j: string]: string | undefined | number | Address }, select_criteria: { mode: string, lsp_id?: string, quote_id?: string, item_id?: string, max_amount?: number } } = {
        client_order_id: billNumber,
        pickup: {
          store_id: storeId,
          rto_otp: rtoOtp
        },
        drop: {
          lat: customerLocation.lat,
          lng: customerLocation.lng,
          address: {
            name: customerLocation.address.name,
            line1: customerLocation.address.line1,
            line2: customerLocation.address.line2,
            city: customerLocation.address.city,
            state: customerLocation.address.state,
          },
          pincode: customerLocation.pincode,
          phone: customerLocation.phone,
          otp: dropOtp
        },
        ready_to_ship: "yes",
        order_amount: orderAmount,
        order_items: [],
        select_criteria: {
          mode: "fastest_agent"
        },
        route: 'shikhar'
      }

      const createUrl = selectedQuote ? '/webui/order/create' : '/webui/order/createasync'
      if (selectedQuote) {
        payload.select_criteria.mode = 'selected_lsp'
        payload.select_criteria.lsp_id = selectedQuote.lsp_id
        payload.select_criteria.item_id = selectedQuote.item_id
        payload.select_criteria.quote_id = quoteId
      }

      return Api<{ status: number, message: string, order?: { id: string }, error_code?: number }>(createUrl, "POST", payload, {token})
    },
    onError(error) {
      toast.error(error.message ||  "Error creating order")
    },
    onSuccess() {
      navigate({to: "/shikhar/orders"})
      toast.success('Order created successfully')
    }
  })

  return <div className="fixed top-0 bottom-0 left-0 right-0 bg-white z-30">
    <div className="h-14 flex px-4 items-center bg-light-blue w-full">
      <div className="flex items-center">
        <img src={backIcon} className="w-5 mr-4" onClick={() => router.history.back()} />
        <p className="font-bold text-lg">Order Details</p>
      </div>
    </div>
    <div className="absolute top-14 bottom-32 overflow-auto w-full p-4 *:mb-4">
      <div>
        <p className="font-bold ml-1 mb-2.5">Package Details</p>
        <div className="flex flex-col p-[16px] items-center justify-between w-full border rounded-lg *:shrink-0">
          <div className="w-full">
            <Select label="Package Content" options={[{ label: "Grocery", value: 'Grocery' }, { label: "Food & Beverages", value: 'F&B' }, { label: "Pharma", value: "Pharma" }]} value={packageType}
              onChange={val => setPackageType(val)} size='max' />
          </div>
          <div className="flex items-center justify-between w-full mt-2">
            <Input label="Bill Number" value={billNumber} onChange={val => setBillNumber(val)} size="small" />
            <Input label={<div className="flex items-center gap-1"><span>Order Amount</span><Popover><PopoverTrigger asChild><button type="button" className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-gray-300" aria-label="Order amount info"><Info size={16} /></button></PopoverTrigger><PopoverContent side="top" align="start" className="w-72 text-sm">This is the amount LSP will refund if item is lost/damaged post pickup</PopoverContent></Popover></div>} type="number" value={orderAmount} onChange={val => setOrderAmount(val)} size="small" />
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center">
        <div className="border rounded-xl px-3.5 py-2.5 flex-1 mr-5">
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <span>Drop OTP</span>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-gray-300"
                  aria-label="Drop OTP info"
                >
                  <Info size={16} />
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" align="start" className="w-72 text-sm">
                Share this OTP with the rider during delivery, It will also be shared with Receiver once order is confirmed.
              </PopoverContent>
            </Popover>
          </div>
          <p className="text-lg text-dark-midnight font-bold">{dropOtp}</p>
        </div>
        <div className="border rounded-xl px-3.5 py-2.5 flex-1">
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <span>RTO OTP</span>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-gray-300"
                  aria-label="RTO OTP info"
                >
                  <Info size={16} />
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" align="start" className="w-72 text-sm">
                Use this OTP if the order is returned and needs RTO verification.
              </PopoverContent>
            </Popover>
          </div>
          <p className="text-lg text-dark-midnight font-bold">{rtoOtp}</p>
        </div>
      </div>
      <div className="bg-gray-200 pl-4 rounded-xl">
        <div className="flex items-center">
          <img src={info} className="w-6 mr-2.5" />
          <div className="py-4 border-b border-gray-300 pr-2">
            <p className="text-sm">Share OTP with the delivery partner at the time of pickup/drop.</p>
          </div>
        </div>
        <div className="flex items-center">
          <img src={info} className="w-6 mr-2.5" />
          <div className="py-4 pr-2.5">
            <p className="text-sm">Drop OTP will be sent to Receiver once order is confirmed.</p>
          </div>
        </div>
      </div>
      <DeliveryOption
        quotes={quotes}
        readonly
        showPriceFilter
        sortQuotesByPrice
        onQuoteSelect={quote => setSelectedQuote(quote)}
        selectedQuoteId={selectedQuote ? `${selectedQuote.lsp_id}::${selectedQuote.item_id}` : null}
      />
    </div>
    <div className="absolute bottom-0 shadow-3xl p-4 w-full">
      <div className="flex items-center mb-3">
        <img src={walletIcon} className="w-6 mr-4" />
        <p className="font-semibold text-dark-midnight">{`Available Wallet Balance: ₹ ${walletBalance?.toFixed(2) || 0}`}</p>
      </div>
      <Button title="Confirm Order" disabled={!packageType || !billNumber || !orderAmount} 
        onClick={() => createOrder.mutate()} loading={createOrder.isPending}/>
    </div>
  </div>
}
